import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaClipboardList,
  FaCloud,
  FaPercent,
  FaShieldAlt,
  FaTasks,
  FaTimes,
  FaSchool,
  FaUsers,
  FaChevronRight,
  FaHeart,
  FaImage,
  FaBullhorn,
  FaBookOpen,
} from "react-icons/fa";

const superAdminMenuSections = [
  { label: "Main navigation", items: [
    { key: "tenantPortfolio", label: "Tenants", icon: FaCloud, tone: "text-sky-400" },
    { key: "platformMetrics", label: "Platform Metrics", icon: FaCloud, tone: "text-violet-400", to: "/super-admin/platform-metrics" },
  ] },
  { label: "Commercial", items: [
    { key: "loginAds", label: "Login Ads", icon: FaBullhorn, tone: "text-cyan-400", to: "/super-admin/login-ads" },
    { key: "discountControls", label: "Discount Controls", icon: FaPercent, tone: "text-emerald-400" },
    { key: "trialRequests", label: "Trial Requests", icon: FaShieldAlt, tone: "text-amber-400" },
    { key: "cancellationRequests", label: "Cancel Requests", icon: FaShieldAlt, tone: "text-rose-400" },
    { key: "schoolLeads", label: "School Leads", icon: FaSchool, tone: "text-orange-400", to: "/super-admin/school-leads" },
  ] },
  { label: "Administration", items: [
    { key: "momentModeration", label: "Moment Moderation", icon: FaHeart, tone: "text-rose-400", to: "/moments" },
    { key: "devActivityTracker", label: "Dev Activity Tracker", icon: FaClipboardList, tone: "text-cyan-400" },
    { key: "customizationRequests", label: "Customization Requests", icon: FaTasks, tone: "text-fuchsia-400", to: "/super-admin/customization-requests" },
    { key: "learningResources", label: "Learning Resources", icon: FaBookOpen, tone: "text-cyan-400", to: "/super-admin/learning-resources" },
    { key: "brandingApprovals", label: "Logo Approvals", icon: FaImage, tone: "text-blue-400", to: "/super-admin/branding-approvals" },
    { key: "employees", label: "Employees", icon: FaUsers, tone: "text-teal-400", to: "/super-admin/employees" },
  ] },
];
const superAdminSubmenuItems = superAdminMenuSections.flatMap((section) => section.items);

const SuperAdminSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("tenantPortfolio");

  const handleSubmenuClick = (item) => {
    setActiveMenu(item.key);

    if (item.to) {
      navigate(item.to);
      setIsMobileOpen(false);
      return;
    }

    const dispatchAction = () => {
      window.dispatchEvent(new CustomEvent("super-admin:select-action", {
        detail: { action: item.key },
      }));
    };

    if (location.pathname !== "/super-admin") {
      navigate("/super-admin");
      window.setTimeout(dispatchAction, 120);
    } else {
      dispatchAction();
    }

    setIsMobileOpen(false);
  };

  React.useEffect(() => {
    const currentItem = superAdminSubmenuItems.find((item) => item.to === location.pathname);
    if (currentItem) setActiveMenu(currentItem.key);
  }, [location.pathname]);

  const SidebarInner = ({ compact = false }) => (
    <div className="flex h-full min-h-0 flex-col bg-[#17202b]">
      {compact && (
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-4">
          <div><p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">ClassNX</p><p className="mt-1 text-sm font-semibold text-white">Control panel</p></div>
          <button type="button" aria-label="Close sidebar" onClick={() => setIsMobileOpen(false)} className="inline-flex rounded-md p-2 text-slate-400 transition hover:bg-white/5 hover:text-white"><FaTimes /></button>
        </div>
      )}
      {!compact && <div className="border-b border-white/5 px-5 py-5"><div className="flex items-center gap-3"><img src={`${process.env.PUBLIC_URL}/logo.png`} alt="ClassNX" className="h-9 w-9 rounded-lg object-cover" /><div><p className="text-sm font-black tracking-tight text-white">Class<span className="text-sky-400">NX</span></p><p className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">Super admin</p></div></div></div>}
      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4 smooth-scrollbar">
        <div className="space-y-6">
          {superAdminMenuSections.map((section) => <section key={section.label}>
            <p className="mb-2 px-3 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">{section.label}</p>
            <div className="space-y-1">{section.items.map((item) => {
            const Icon = item.icon;
            const isActive = activeMenu === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleSubmenuClick(item)}
                className={`group flex w-full items-center gap-3 rounded-md border-l-2 px-3 py-2.5 text-left text-[13px] font-medium transition ${isActive ? "border-cyan-400 bg-white/[0.08] text-white" : "border-transparent text-slate-400 hover:bg-white/[0.04] hover:text-slate-100"}`}
              >
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/[0.05] text-xs ${item.tone}`}><Icon /></span>
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                <FaChevronRight className={`text-[8px] transition ${isActive ? "text-cyan-300" : "text-slate-600 group-hover:text-slate-400"}`} />
              </button>
            );
          })}</div>
          </section>)}
        </div>
      </nav>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsMobileOpen(true)}
        className="theme-menu-fab fixed bottom-6 right-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full border shadow-lg lg:hidden"
        title="Open menu"
      >
        <FaBars className="text-xl" />
      </button>

      {isMobileOpen && (
        <button
          type="button"
          aria-label="Close menu overlay"
          onClick={() => setIsMobileOpen(false)}
          className="theme-menu-overlay fixed inset-0 z-30 lg:hidden"
        />
      )}

      <aside
        className="fixed bottom-[4.25rem] left-0 top-[4.5rem] z-40 w-[82vw] max-w-[17rem] overflow-hidden border-r border-slate-800 bg-[#17202b] text-slate-100 shadow-2xl lg:hidden"
        style={{ transform: isMobileOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 300ms ease" }}
      >
        <div className="h-full overflow-y-auto smooth-scrollbar">
          <SidebarInner compact />
        </div>
      </aside>

      <aside className="fixed bottom-0 left-0 top-0 z-[60] hidden w-[248px] flex-col overflow-hidden border-r border-slate-800 bg-[#17202b] text-slate-100 shadow-xl lg:flex">
        <div className="min-h-0 flex-1 overflow-y-auto smooth-scrollbar">
          <SidebarInner />
        </div>
      </aside>
    </>
  );
};

export default SuperAdminSidebar;
